import React, { useState, useContext } from "react";
import PropTypes from "prop-types";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ConfirmationModal from "../ConfirmationModal";
import { Menu, MenuItem, Grid, Popover, IconButton, ListItemIcon, ListItemText, makeStyles } from "@material-ui/core";
import { ReplyMessageContext } from "../../context/ReplyingMessage/ReplyingMessageContext";
import EditMessageModal from "../EditMessageModal";
import { ForwardMessageContext } from "../../context/ForwarMessage/ForwardMessageContext";
import ForwardModal from "../../components/ForwardMessageModal";
import {toast} from "react-toastify";
import toastError from "../../errors/toastError";
import { Forward, Pencil, Reply, SmilePlus, Trash2 } from "lucide-react";

const useStyles = makeStyles((theme) => ({
	menuPaper: {
	  minWidth: 180,
	  padding: "5px 0 4px",
	  borderRadius: 12,
	  background: theme.mode === "dark" ? "#121212" : "#ffffff",
	  boxShadow: theme.mode === "dark" ? "0 10px 30px rgba(0,0,0,0.45)" : "0 10px 30px rgba(0,0,0,0.14)",
	},
	menuItem: {
	  minHeight: 32,
	  padding: "5px 16px",
	  gap: 8,
	  fontSize: 13,
	  color: theme.mode === "dark" ? "#e9edef" : "#111b21",
	  "&:hover": {
		backgroundColor: theme.mode === "dark" ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.04)",
	  },
	},
	menuItemDanger: {
	  color: "#ef5350",
	  "& $menuIcon": {
		color: "#ef5350",
	  },
	},
	menuIcon: {
	  minWidth: 22,
	  color: theme.mode === "dark" ? "#aebac1" : "#54656f",
	},
	menuText: {
	  "& span": {
	    fontSize: 13,
		fontWeight: 400,
	  },
	},
	iconButton: {
	  padding: '4px', // Ajuste o valor conforme necessário
	},
	gridContainer: {
	  padding: '10px',
	  justifyContent: 'center',
	},
	addCircleButton: {
	  padding: '8px',
	  color: theme.mode === "dark" ? "#e9edef" : "#111b21",
	  backgroundColor: theme.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgb(242 242 247)",
	  "&:hover": {
		backgroundColor: theme.mode === "dark" ? "rgba(255,255,255,0.14)" : "rgba(0,0,0,0.08)",
	  },
	},
	popoverContent: {
	  maxHeight: '300px', // Ajuste conforme necessário
	  overflowY: 'auto',
	  '&::-webkit-scrollbar': {
		width: '0.4em',
		height: '0.4em',
	  },
	  '&::-webkit-scrollbar-thumb': {
		backgroundColor: 'rgba(0,0,0,.1)',
		borderRadius: '50px',
	  },
	  '&::-webkit-scrollbar-track': {
		boxShadow: 'inset 0 0 6px rgba(0,0,0,0.00)',
		webkitBoxShadow: 'inset 0 0 6px rgba(0,0,0,0.00)',
	  },
	},
	hideScrollbar: {
	  maxHeight: '300px',
	  overflow: 'hidden',
	},
  }));

const getBasename = (path = "") => {
	const cleanPath = String(path || "").split(/[?#]/)[0];
	const index = cleanPath.lastIndexOf("/");
	return index >= 0 ? cleanPath.substring(index + 1) : cleanPath;
};

const canEditMessage = (message) => {
	if (!message?.fromMe || message?.isDeleted) return false;

	const mediaType = message.mediaType || "";
	const body = (message.body || "").trim();
	if (!body) return false;

	const blockedTypes = [
		"audio",
		"document",
		"locationMessage",
		"contactMessage",
		"reactionMessage",
		"template",
		"adMetaPreview",
		"call_log",
	];

	if (["image", "video"].includes(mediaType)) {
		const mediaName = getBasename(message.mediaUrl).trim();
		return body !== mediaName;
	}

	return !blockedTypes.includes(mediaType);
};

const MessageOptionsMenu = ({ message, menuOpen, handleClose, anchorEl, contactId, onMessageUpdated, onReactionAdded }) => {
	const classes = useStyles();
	const { setReplyingMessage } = useContext(ReplyMessageContext);
	const [confirmationOpen, setConfirmationOpen] = useState(false);
	const [confirmationEditOpen, setEditMessageOpenModal] = useState(false);
	const [reactionAnchorEl, setReactionAnchorEl] = useState(null);
	const [moreAnchorEl, setMoreAnchorEl] = useState(null);
	const {
		showSelectMessageCheckbox,
		setShowSelectMessageCheckbox,
		selectedMessages,
		setSelectedMessages,
		forwardMessageModalOpen,
		setForwardMessageModalOpen } = useContext(ForwardMessageContext);
		

	const handleDeleteMessage = async () => {
		try {
			await api.delete(`/messages/${message.id}`);
		} catch (err) {
			toastError(err);
		}
	};

	const openReactionsMenu = (event) => {
		setReactionAnchorEl(event.currentTarget);
		handleClose();
	};
	
	const closeReactionsMenu = () => {
		setReactionAnchorEl(null);
	};

	const openMoreReactionsMenu = (event) => {
		setMoreAnchorEl(event.currentTarget);
		closeReactionsMenu();  // Fechar o primeiro popover
	};

	const closeMoreReactionsMenu = () => {
		setMoreAnchorEl(null);
	};

	const closeAllMenus = () => {
		handleClose();
		closeReactionsMenu();
		closeMoreReactionsMenu();
	};

	const handleReactToMessage = async (reactionType) => {
		try {
			await api.post(`/messages/${message.id}/reactions`, { type: reactionType });
			if (onReactionAdded) onReactionAdded(message.id, reactionType);
			toast.success(i18n.t("reactions.successMessage"));
		} catch (err) {
			toastError(err);
		}
		// Fechar todos os menus após enviar a reação
		closeAllMenus();
	};

	// Array de emojis
    const availableReactions = [
		'😀', '😂', '❤️', '👍', '🎉', '😢', '😮', '😡', '👏', '🔥',
        '🥳', '😎', '🤩', '😜', '🤔', '🙄', '😴', '😇', '🤯', '💩',
        '🤗', '🤫', '🤭', '🤓', '🤪', '🤥', '🤡', '🤠', '🤢', '🤧',
        '😷', '🤕', '🤒', '👻', '💀', '☠️', '👽', '👾', '🤖', '🎃',
        '😺', '😸', '😹', '😻', '😼', '😽', '🙀', '😿', '😾', '🙈',
        '🙉', '🙊', '🐵', '🐒', '🦍', '🐶', '🐕', '🐩', '🐺', '🦊',
        '🦝', '🐱', '🐈', '🦁', '🐯', '🐅', '🐆', '🐴', '🐎', '🦄'
	];	

	const handleSetShowSelectCheckbox = () => {
		setSelectedMessages([message]);
		setShowSelectMessageCheckbox(true);
		handleClose();
	};

	const hanldeReplyMessage = () => {
		setReplyingMessage(message);
		handleClose();
	};

	const handleOpenConfirmationModal = e => {
		setConfirmationOpen(true);
		handleClose();
	};

	const handleOpenEditMessageModal = e => {
		setEditMessageOpenModal(true);
		handleClose();
	};

	return (
		<>
			<ForwardModal
			modalOpen={forwardMessageModalOpen}
			messages={selectedMessages}
			onClose={(e) => {
				setForwardMessageModalOpen(false);
				setShowSelectMessageCheckbox(false);
				setSelectedMessages([]);
			}}
				/>
			<ConfirmationModal
				title={i18n.t("messageOptionsMenu.confirmationModal.title")}
				open={confirmationOpen}
				onClose={setConfirmationOpen}
				onConfirm={handleDeleteMessage}
			>
				{i18n.t("messageOptionsMenu.confirmationModal.message")}
			</ConfirmationModal>
			<EditMessageModal
				open={confirmationEditOpen}
				onClose={setEditMessageOpenModal}
				message={message}
				onMessageUpdated={onMessageUpdated}
			/>
			<Menu
				anchorEl={anchorEl}
				getContentAnchorEl={null}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "right",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "right",
				}}
				open={menuOpen}
				onClose={handleClose}
				PaperProps={{ className: classes.menuPaper }}
			>
				<MenuItem onClick={handleSetShowSelectCheckbox} className={classes.menuItem}>
					<ListItemIcon className={classes.menuIcon}><Forward size={17} /></ListItemIcon>
					<ListItemText className={classes.menuText} primary={i18n.t("messageOptionsMenu.forward")} />
				</MenuItem>
				{canEditMessage(message) && (
					<MenuItem onClick={handleOpenEditMessageModal} className={classes.menuItem}>
						<ListItemIcon className={classes.menuIcon}><Pencil size={17} /></ListItemIcon>
						<ListItemText className={classes.menuText} primary={i18n.t("messageOptionsMenu.edit")} />
					</MenuItem>
				)}
				{message.fromMe && (
					<MenuItem onClick={handleOpenConfirmationModal} className={`${classes.menuItem} ${classes.menuItemDanger}`}>
						<ListItemIcon className={classes.menuIcon}><Trash2 size={17} /></ListItemIcon>
						<ListItemText className={classes.menuText} primary={i18n.t("messageOptionsMenu.delete")} />
					</MenuItem>
				)}
				<MenuItem onClick={hanldeReplyMessage} className={classes.menuItem}>
					<ListItemIcon className={classes.menuIcon}><Reply size={17} /></ListItemIcon>
					<ListItemText className={classes.menuText} primary={i18n.t("messageOptionsMenu.reply")} />
				</MenuItem>
				<MenuItem onClick={openReactionsMenu} className={classes.menuItem}>
					<ListItemIcon className={classes.menuIcon}><SmilePlus size={17} /></ListItemIcon>
					<ListItemText className={classes.menuText} primary={i18n.t("messageOptionsMenu.react")} />
				</MenuItem>
			</Menu>
			<Popover
					open={Boolean(reactionAnchorEl)}
					anchorEl={reactionAnchorEl}
					onClose={closeReactionsMenu}
					anchorOrigin={{
						vertical: 'bottom',
						horizontal: 'right',
					}}
					transformOrigin={{
						vertical: 'top',
						horizontal: 'right',
					}}
					PaperProps={{
						style: { width: 'auto', maxWidth: '380px', borderRadius: '50px'  }
					}}
				>
					<div className={classes.hideScrollbar}>
					<Grid container spacing={1} className={classes.gridContainer}>
						{availableReactions.slice(0, 6).map(reaction => (
							<Grid item key={reaction}>
								<IconButton className={classes.iconButton} onClick={() => handleReactToMessage(reaction)}>
									{reaction}
								</IconButton>
							</Grid>
						))}
						<Grid item>
						<IconButton className={classes.addCircleButton} onClick={openMoreReactionsMenu}>
								<SmilePlus size={22} strokeWidth={1.8} />
							</IconButton>
						</Grid>
					</Grid>
					</div>
				</Popover>
				<Popover
					open={Boolean(moreAnchorEl)}
					anchorEl={moreAnchorEl}
					onClose={closeMoreReactionsMenu}
					anchorOrigin={{
						vertical: 'bottom',
						horizontal: 'center',
					}}
					transformOrigin={{
						vertical: 'top',
						horizontal: 'center',
					}}
					PaperProps={{
						style: { width: 'auto', maxWidth: '400px', borderRadius: '6px' }
					}}
				>
					<div className={classes.popoverContent}>
					<Grid container spacing={1} className={classes.gridContainer}>
						{availableReactions.map(reaction => (
							<Grid item key={reaction}>
								<IconButton className={classes.iconButton} onClick={() => handleReactToMessage(reaction)}>
									{reaction}
								</IconButton>
							</Grid>
						))}
					</Grid>
					</div>
				</Popover>
		</>
	);
};

MessageOptionsMenu.propTypes = {
    message: PropTypes.object,
    menuOpen: PropTypes.bool.isRequired,
    handleClose: PropTypes.func.isRequired,
    anchorEl: PropTypes.object,
    onMessageUpdated: PropTypes.func,
    onReactionAdded: PropTypes.func,
    onReaction: PropTypes.func, // Callback opcional chamado após uma reação
    availableReactions: PropTypes.arrayOf(PropTypes.string) // Lista opcional de reações disponíveis
}

export default MessageOptionsMenu;
